import { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  CanvasComponent,
  CanvasContainer,
  CanvasContentId,
  CanvasWorkspace,
  SymbolGroup,
} from 'types/canvas';
import { CANVAS_COMPONENT_META } from 'config/canvasComponentCatalog';
import useToast from 'hooks/useToast';
import { useCanvasAPI, ContainerInput } from './useCanvasAPI';

interface UseSymbolGroupsArgs {
  /** The currently active workspace (source of truth for `groups`). */
  activeWorkspace: CanvasWorkspace | undefined;
  /**
   * Setter for the workspaces React state. We mutate the active workspace's
   * `groups` field optimistically; a failed PUT rolls the change back.
   */
  setWorkspaces: React.Dispatch<React.SetStateAction<CanvasWorkspace[]>>;
  /** Same instance Canvas.tsx uses for layout sync. Avoids duplicate hooks. */
  api: ReturnType<typeof useCanvasAPI>;
  /**
   * Wraps a promise in the existing "syncing → saved" status indicator.
   * Provided by Canvas.tsx so group writes show up the same way layout
   * writes do.
   */
  withSyncStatus: <T>(fn: () => Promise<T>) => Promise<T>;
  /**
   * Serializer that strips client-only container fields and produces the
   * `ContainerInput[]` shape `api.syncWorkspaceLayout` expects. Already
   * exists in Canvas.tsx — we accept it as a prop so we don't duplicate.
   */
  serializeLayout: (workspace: CanvasWorkspace) => ContainerInput[];
  /**
   * Optional pre-mutation hook used by Canvas.tsx to snapshot the
   * workspace state into its undo stack before each group mutation.
   */
  onBeforeMutation?: () => void;
}

/**
 * Default color used when the user creates a group without picking one.
 * Cycles through this palette skipping colors already in use so brand-new
 * groups stay visually distinct without forcing the user to choose.
 */
export const DEFAULT_PALETTE = [
  '#ef5350', // red
  '#ff9800', // orange
  '#ffd54f', // amber
  '#66bb6a', // green
  '#26a69a', // teal
  '#42a5f5', // blue
  '#ab47bc', // purple
  '#ec407a', // pink
] as const;

const pickNextDefaultColor = (existing: SymbolGroup[]): string => {
  const used = new Set(existing.map((g) => g.color.toLowerCase()));
  for (const c of DEFAULT_PALETTE) {
    if (!used.has(c.toLowerCase())) {
      return c;
    }
  }
  // All palette colors used — fall back to the first.
  return DEFAULT_PALETTE[0];
};

export interface UseSymbolGroupsResult {
  groups: SymbolGroup[];

  groupForComponent: (componentId: number) => SymbolGroup | undefined;
  isComponentEligible: (contentId: CanvasContentId) => boolean;

  createGroup: (
    name: string,
    color: string,
    initialMember?: number,
  ) => Promise<SymbolGroup | null>;
  renameGroup: (groupId: string, name: string) => Promise<void>;
  recolorGroup: (groupId: string, color: string) => Promise<void>;
  deleteGroup: (groupId: string) => Promise<void>;
  /**
   * Atomically removes the component from any prior group before adding it
   * to `groupId`. Pass `null` to just remove. No-ops if the component is
   * already in `groupId`.
   */
  setComponentGroup: (
    componentId: number,
    groupId: string | null,
  ) => Promise<void>;
  /**
   * Used by Canvas.tsx after a successful component DELETE so the local
   * groups state doesn't briefly show a chip on a deleted component while
   * the next workspace fetch round-trips.
   */
  stripComponentLocally: (componentId: number) => void;

  /**
   * Returns the symbol the group is currently "on", derived from any of
   * its members' `config.sym`. Returns `undefined` for empty groups or
   * when no member has a sym set.
   */
  getGroupSym: (groupId: string) => string | undefined;
  /**
   * Pushes `sym` to every member of `groupId`, respecting each member's
   * `supportedSyms` whitelist (members that can't render this sym keep
   * their previous one — the broadcast skips them). Optimistic + persists
   * via `api.updateComponent` per member.
   */
  broadcastSymToGroup: (groupId: string, sym: string) => void;

  /** Default-color helper exposed for the create dialog's initial state. */
  pickDefaultColor: () => string;

  manageDialogOpen: boolean;
  openManageDialog: () => void;
  closeManageDialog: () => void;
}

export function useSymbolGroups({
  activeWorkspace,
  setWorkspaces,
  api,
  withSyncStatus,
  serializeLayout,
  onBeforeMutation,
}: UseSymbolGroupsArgs): UseSymbolGroupsResult {
  const { openToast } = useToast();
  const [manageDialogOpen, setManageDialogOpen] = useState(false);

  const groups = useMemo<SymbolGroup[]>(
    () => activeWorkspace?.groups ?? [],
    [activeWorkspace?.groups],
  );

  const componentToGroup = useMemo(() => {
    const map = new Map<number, SymbolGroup>();
    for (const g of groups) {
      for (const cid of g.componentIds) {
        map.set(cid, g);
      }
    }
    return map;
  }, [groups]);

  const groupForComponent = useCallback(
    (componentId: number) => componentToGroup.get(componentId),
    [componentToGroup],
  );

  const isComponentEligible = useCallback(
    (contentId: CanvasContentId) =>
      CANVAS_COMPONENT_META[contentId]?.singleSymbol === true,
    [],
  );

  /**
   * Persists `nextGroups` for the active workspace via the existing
   * layout-sync PUT. Optimistic: writes local state first, rolls back on
   * failure and surfaces a toast. Returns true on success.
   */
  const persistGroups = useCallback(
    async (nextGroups: SymbolGroup[], operation: string): Promise<boolean> => {
      if (activeWorkspace == null) {
        return false;
      }
      const workspaceId = activeWorkspace.id;
      const previous = activeWorkspace.groups ?? [];

      // Snapshot for undo *before* the optimistic mutation lands.
      onBeforeMutation?.();

      // Optimistic local update first so the UI reflects immediately.
      setWorkspaces((prev) =>
        prev.map((ws) =>
          ws.id === workspaceId ? { ...ws, groups: nextGroups } : ws,
        ),
      );

      const layout = serializeLayout(activeWorkspace);
      const synced = await withSyncStatus(() =>
        api.syncWorkspaceLayout(workspaceId, undefined, layout, nextGroups),
      );

      if (synced == null) {
        // Roll back.
        setWorkspaces((prev) =>
          prev.map((ws) =>
            ws.id === workspaceId ? { ...ws, groups: previous } : ws,
          ),
        );
        openToast({
          type: 'error',
          message: `Failed to ${operation}. Please try again.`,
        });
        return false;
      }
      return true;
    },
    [
      activeWorkspace,
      setWorkspaces,
      api,
      withSyncStatus,
      serializeLayout,
      openToast,
      onBeforeMutation,
    ],
  );

  const createGroup = useCallback(
    async (
      name: string,
      color: string,
      initialMember?: number,
    ): Promise<SymbolGroup | null> => {
      if (activeWorkspace == null) {
        return null;
      }
      const trimmed = name.trim();
      if (trimmed.length === 0) {
        openToast({ type: 'error', message: 'Group name is required.' });
        return null;
      }

      const newGroup: SymbolGroup = {
        id: uuidv4(),
        name: trimmed,
        color,
        // If the initial member is already in another group, strip first
        // (one-component-one-group invariant).
        componentIds: initialMember != null ? [initialMember] : [],
      };

      const stripped = (activeWorkspace.groups ?? []).map((g) =>
        initialMember != null
          ? {
              ...g,
              componentIds: g.componentIds.filter(
                (cid) => cid !== initialMember,
              ),
            }
          : g,
      );
      const nextGroups = [...stripped, newGroup];

      const ok = await persistGroups(nextGroups, 'create group');
      return ok ? newGroup : null;
    },
    [activeWorkspace, persistGroups, openToast],
  );

  const renameGroup = useCallback(
    async (groupId: string, name: string): Promise<void> => {
      if (activeWorkspace == null) {
        return;
      }
      const trimmed = name.trim();
      if (trimmed.length === 0) {
        openToast({ type: 'error', message: 'Group name is required.' });
        return;
      }
      const next = (activeWorkspace.groups ?? []).map((g) =>
        g.id === groupId ? { ...g, name: trimmed } : g,
      );
      await persistGroups(next, 'rename group');
    },
    [activeWorkspace, persistGroups, openToast],
  );

  const recolorGroup = useCallback(
    async (groupId: string, color: string): Promise<void> => {
      if (activeWorkspace == null) {
        return;
      }
      const next = (activeWorkspace.groups ?? []).map((g) =>
        g.id === groupId ? { ...g, color } : g,
      );
      await persistGroups(next, 'update group color');
    },
    [activeWorkspace, persistGroups],
  );

  const deleteGroup = useCallback(
    async (groupId: string): Promise<void> => {
      if (activeWorkspace == null) {
        return;
      }
      const next = (activeWorkspace.groups ?? []).filter(
        (g) => g.id !== groupId,
      );
      await persistGroups(next, 'delete group');
    },
    [activeWorkspace, persistGroups],
  );

  const setComponentGroup = useCallback(
    async (componentId: number, groupId: string | null): Promise<void> => {
      if (activeWorkspace == null) {
        return;
      }
      const current = activeWorkspace.groups ?? [];
      const existing = componentToGroup.get(componentId);

      // No-op when already in the requested group.
      if (existing?.id === groupId) {
        return;
      }
      // No-op when removing from no group.
      if (groupId == null && existing == null) {
        return;
      }

      const next = current
        .map((g) => {
          // Strip from any prior membership.
          if (g.componentIds.includes(componentId)) {
            return {
              ...g,
              componentIds: g.componentIds.filter((c) => c !== componentId),
            };
          }
          return g;
        })
        .map((g) =>
          // Add to the new group.
          g.id === groupId
            ? { ...g, componentIds: [...g.componentIds, componentId] }
            : g,
        );

      // When linking to a group whose existing members already share a
      // sym, the joining component should adopt that sym instantly so the
      // group stays in sync from the moment it joins. We figure out the
      // inherited sym + the target component reference before any state
      // mutations so we can roll back cleanly on failure.
      let symInherit: {
        containerId: number;
        component: CanvasComponent;
        newSym: string;
      } | null = null;
      if (groupId != null) {
        const targetGroup = current.find((g) => g.id === groupId);
        const otherMember = targetGroup?.componentIds.find(
          (c) => c !== componentId,
        );
        if (otherMember != null) {
          let otherSym: string | undefined;
          let ourContainerId: number | undefined;
          let ourComp: CanvasComponent | undefined;
          for (const container of activeWorkspace.layout) {
            if (container.components == null) {
              continue;
            }
            for (const comp of container.components) {
              if (comp.id === otherMember) {
                otherSym = comp.config.sym;
              }
              if (comp.id === componentId) {
                ourContainerId = container.id;
                ourComp = comp;
              }
            }
          }
          if (
            otherSym != null &&
            ourComp != null &&
            ourContainerId != null &&
            ourComp.config.sym !== otherSym
          ) {
            // Respect the joining component's sym whitelist — if the
            // group's sym isn't supported (e.g. linking an indices-only
            // chart to an equity-symbol group), skip the inherit instead
            // of feeding it bad data. The link itself still goes through.
            const supported =
              CANVAS_COMPONENT_META[ourComp.contentId]?.supportedSyms;
            if (supported == null || supported.includes(otherSym)) {
              symInherit = {
                containerId: ourContainerId,
                component: ourComp,
                newSym: otherSym,
              };
            }
          }
        }
      }

      const ok = await persistGroups(
        next,
        groupId == null ? 'remove from group' : 'add to group',
      );

      // Sync the inherited sym only after the group write lands so a
      // failed group write doesn't leave the component with a mismatched
      // sym and no group membership to justify it.
      if (ok && symInherit != null) {
        const { containerId, component, newSym } = symInherit;
        const workspaceId = activeWorkspace.id;
        const previousSym = component.config.sym;

        // Optimistic local sym update on the joining component.
        setWorkspaces((prev) =>
          prev.map((ws) => {
            if (ws.id !== workspaceId) {
              return ws;
            }
            return {
              ...ws,
              layout: ws.layout.map((c) => {
                if (c.components == null) {
                  return c;
                }
                return {
                  ...c,
                  components: c.components.map((comp) =>
                    comp.id === componentId
                      ? { ...comp, config: { ...comp.config, sym: newSym } }
                      : comp,
                  ),
                };
              }),
            };
          }),
        );

        const synced = await withSyncStatus(() =>
          api.updateComponent(containerId, component.id, {
            label: component.label,
            config: { ...component.config, sym: newSym },
          }),
        );

        if (synced == null) {
          // Roll the sym change back; the membership stays since the user
          // explicitly asked for it and the group write already succeeded.
          setWorkspaces((prev) =>
            prev.map((ws) => {
              if (ws.id !== workspaceId) {
                return ws;
              }
              return {
                ...ws,
                layout: ws.layout.map((c) => {
                  if (c.components == null) {
                    return c;
                  }
                  return {
                    ...c,
                    components: c.components.map((comp) =>
                      comp.id === componentId
                        ? {
                            ...comp,
                            config: { ...comp.config, sym: previousSym },
                          }
                        : comp,
                    ),
                  };
                }),
              };
            }),
          );
          openToast({
            type: 'error',
            message: 'Linked to group, but failed to sync the symbol.',
          });
        }
      }
    },
    [
      activeWorkspace,
      componentToGroup,
      persistGroups,
      setWorkspaces,
      withSyncStatus,
      api,
      openToast,
    ],
  );

  const stripComponentLocally = useCallback(
    (componentId: number) => {
      if (activeWorkspace == null) {
        return;
      }
      const workspaceId = activeWorkspace.id;
      setWorkspaces((prev) =>
        prev.map((ws) => {
          if (ws.id !== workspaceId) {
            return ws;
          }
          const cleaned = (ws.groups ?? []).map((g) => ({
            ...g,
            componentIds: g.componentIds.filter((c) => c !== componentId),
          }));
          return { ...ws, groups: cleaned };
        }),
      );
    },
    [activeWorkspace, setWorkspaces],
  );

  const pickDefaultColor = useCallback(
    () => pickNextDefaultColor(groups),
    [groups],
  );

  const getGroupSym = useCallback(
    (groupId: string): string | undefined => {
      if (activeWorkspace == null) {
        return undefined;
      }
      const group = (activeWorkspace.groups ?? []).find(
        (g) => g.id === groupId,
      );
      if (group == null || group.componentIds.length === 0) {
        return undefined;
      }
      const memberSet = new Set(group.componentIds);
      // First match wins — every broadcast keeps members in sync, so any
      // member's sym is the group's sym.
      for (const container of activeWorkspace.layout) {
        if (container.components == null) {
          continue;
        }
        for (const comp of container.components) {
          if (
            memberSet.has(comp.id) &&
            comp.config.sym != null &&
            comp.config.sym !== ''
          ) {
            return comp.config.sym;
          }
        }
      }
      return undefined;
    },
    [activeWorkspace],
  );

  const broadcastSymToGroup = useCallback(
    (groupId: string, sym: string): void => {
      if (activeWorkspace == null) {
        return;
      }
      const group = (activeWorkspace.groups ?? []).find(
        (g) => g.id === groupId,
      );
      if (group == null || group.componentIds.length === 0) {
        return;
      }
      const memberSet = new Set(group.componentIds);
      const workspaceId = activeWorkspace.id;

      // Walk the layout once: update every member's sym in place (skipping
      // members whose chart can't render this sym), and collect the
      // (container, component) tuples we need to PUT.
      const broadcastTargets: Array<{
        container: CanvasContainer;
        component: CanvasComponent;
      }> = [];
      const updatedLayout = activeWorkspace.layout.map((item) => {
        if (item.components == null) {
          return item;
        }
        let containerChanged = false;
        const updatedComponents = item.components.map((comp) => {
          if (!memberSet.has(comp.id)) {
            return comp;
          }
          const supported =
            CANVAS_COMPONENT_META[comp.contentId]?.supportedSyms;
          if (supported != null && !supported.includes(sym)) {
            return comp;
          }
          if (comp.config.sym === sym) {
            return comp;
          }
          containerChanged = true;
          const updated: CanvasComponent = {
            ...comp,
            config: { ...comp.config, sym },
          };
          broadcastTargets.push({ container: item, component: updated });
          return updated;
        });
        return containerChanged
          ? { ...item, components: updatedComponents }
          : item;
      });

      if (broadcastTargets.length === 0) {
        return;
      }

      setWorkspaces((prev) =>
        prev.map((ws) =>
          ws.id === workspaceId ? { ...ws, layout: updatedLayout } : ws,
        ),
      );

      void withSyncStatus(() =>
        Promise.all(
          broadcastTargets.map(({ container, component }) =>
            api.updateComponent(container.id, component.id, {
              label: component.label,
              config: component.config,
            }),
          ),
        ),
      );
    },
    [activeWorkspace, setWorkspaces, withSyncStatus, api],
  );

  const openManageDialog = useCallback(() => setManageDialogOpen(true), []);
  const closeManageDialog = useCallback(() => setManageDialogOpen(false), []);

  return {
    groups,
    groupForComponent,
    isComponentEligible,
    createGroup,
    renameGroup,
    recolorGroup,
    deleteGroup,
    setComponentGroup,
    stripComponentLocally,
    getGroupSym,
    broadcastSymToGroup,
    pickDefaultColor,
    manageDialogOpen,
    openManageDialog,
    closeManageDialog,
  };
}
